/* What a contract adds to the company it is a bet on.
 *
 * A position's weight is its market value over the book's total (§5.1), and
 * market value counts shares. That is right for what it measures and wrong as
 * the only thing measured, because a perpetual on the same company is exposure
 * to that company too — bought with margin rather than with cash, but moving
 * with the same price.
 *
 * Left alone, the arithmetic runs backwards. Margin and unrealised sit in the
 * book's total (§5.1), so they are in every weight's denominator and in no
 * weight's numerator. Open a contract on a name already held and that name's
 * reported weight goes *down*: the denominator grew, the numerator did not.
 * This book holds 2.76 shares of MU against a contract of nearly six thousand
 * dollars of notional, and the ring, the cap band and the theme limit all see
 * the 2.76 shares.
 *
 * ── which number is the exposure ──────────────────────────────────────────
 *
 * Notional, not margin. Margin is what can be lost, and the book already counts
 * it — it is cash posted at the exchange and it is in the total. Notional is
 * what moves: a five-times contract with a thousand dollars behind it gains and
 * loses like five thousand dollars of the share, and that is the number that
 * belongs beside a shareholding when the question is how big the bet on this
 * company is.
 *
 * The notional used is the one at the current mark rather than at entry.
 * `sizeUsdt` is the entry notional and has to stay that, because §5.1's
 * unrealised formula is only exact against it — but the question here is what
 * the position is worth of this company *now*, which is the same question a
 * shareholding answers with today's price.
 *
 * ── signs ────────────────────────────────────────────────────────────────
 *
 * A short subtracts. Holding the shares and shorting the contract is a smaller
 * bet on the company than holding the shares alone, and reporting it as a
 * larger one would be worse than saying nothing. It follows that a combined
 * exposure can come out below the shareholding, and on a short with no shares
 * it can come out negative — which is what being net short is, and the screens
 * say so rather than clamping it to zero.
 *
 * ── what this deliberately does not do ───────────────────────────────────
 *
 * It does not touch `weight`. The share weight is the answer to "how much of
 * the book is this holding", it is the number the ring is drawn from and the
 * number every cap band was written against, and folding leverage into it would
 * change the meaning of a figure that appears on every screen. The exposure is
 * a second reading, named as one, shown where the difference matters. */

import { displayName } from './mexcAccount';
import type { PerpView } from './derive';

export interface ContractExposure {
  /** The company or coin, as the book names it. */
  ticker: string;
  /** Signed: long adds, short subtracts. In USD — every contract in this book
      is quoted in USDT and the book carries USDT at parity, the same as the
      margin already in its total. */
  notionalUsd: number;
  /** Cash posted against these contracts. Already inside the book's total; kept
      here so a screen can say what the exposure was bought with. */
  marginUsd: number;
  /** How many open contracts stand behind the figure. */
  count: number;
}

/** The company a contract is a bet on.
 *
 * `display` is what the exchange's own position card prints and what the book
 * shows, and it is stored on the record. A contract written before that field
 * was filled falls back to the symbol, which carries the same name under the
 * suffix `displayName` exists to strip. */
export function tickerOfContract(p: Pick<PerpView, 'display' | 'symbol'>): string {
  const named = (p.display ?? '').trim();
  return (named === '' ? displayName(p.symbol) : named).toUpperCase();
}

/** One row per company that has open contracts, biggest bet first.
 *
 * Takes the views rather than the records because the notional at the mark is
 * derived there, and a contract whose mark has not arrived carries its entry
 * notional — which is the last thing known about it rather than a guess. */
export function contractExposure(perps: PerpView[]): ContractExposure[] {
  const byTicker = new Map<string, ContractExposure>();

  for (const p of perps) {
    if (p.closedAt) continue;
    const ticker = tickerOfContract(p);
    if (ticker === '') continue;

    const row = byTicker.get(ticker)
      ?? { ticker, notionalUsd: 0, marginUsd: 0, count: 0 };
    row.notionalUsd += p.notionalNowUsd * (p.side === 'short' ? -1 : 1);
    row.marginUsd += p.marginUsdt;
    row.count += 1;
    byTicker.set(ticker, row);
  }

  return [...byTicker.values()]
    .sort((a, b) => Math.abs(b.notionalUsd) - Math.abs(a.notionalUsd));
}

/** Signed notional by ticker, for the screens and the theme rollup. */
export function notionalByTicker(rows: ContractExposure[]): Map<string, number> {
  return new Map(rows.map((r) => [r.ticker, r.notionalUsd]));
}
